import React, { useState } from 'react';
import { Container } from '../components/layouts';
import { Logo } from '../brand';
import { ThemeToggle } from '../components/ThemeToggle';
import { surfaces, textColors, typography, borders } from '../tokens';
import TokenShowcase from './sections/TokenShowcase';
import ButtonShowcase from './sections/ButtonShowcase';
import InputShowcase from './sections/InputShowcase';
import CardShowcase from './sections/CardShowcase';
import ModalShowcase from './sections/ModalShowcase';
import LayoutShowcase from './sections/LayoutShowcase';
import IconShowcase from './sections/IconShowcase';
import BrandShowcase from './sections/BrandShowcase';

/**
 * ShowcasePage
 * 
 * Visual showcase of the MyVitae Design System.
 * This is an internal developer tool for quick reference and exploration.
 * 
 * WARNING: This page is for INTERNAL USE ONLY and should not be accessible
 * to end users in production.
 */
const ShowcasePage = () => {
  const [activeSection, setActiveSection] = useState('intro');

  const sections = [
    { id: 'intro', name: 'Introduction', component: null },
    { id: 'dark-mode', name: '🌙 Dark Mode Guide', component: null },
    { id: 'tokens', name: 'Design Tokens', component: TokenShowcase },
    { id: 'brand', name: 'Brand', component: BrandShowcase },
    { id: 'buttons', name: 'Buttons', component: ButtonShowcase },
    { id: 'inputs', name: 'Form Inputs', component: InputShowcase },
    { id: 'cards', name: 'Cards', component: CardShowcase },
    { id: 'modals', name: 'Modals', component: ModalShowcase },
    { id: 'layouts', name: 'Layout', component: LayoutShowcase },
    { id: 'icons', name: 'Icons', component: IconShowcase },
  ];

  const ActiveComponent = sections.find(s => s.id === activeSection)?.component;

  return (
    <div className={`min-h-screen ${surfaces.secondary}`}>
      {/* Header */}
      <header className={`${surfaces.primary} border-b ${borders.default} sticky top-0 z-50 shadow-sm`}>
        <Container size="7xl">
          <div className="flex items-center justify-between py-4">
            <div className="flex items-center gap-4">
              <Logo iconSize="w-8 h-8" textSize="text-xl" />
              <div className={`h-8 w-px ${borders.strong}`} />
              <h1 className={`font-heading text-xl font-bold ${textColors.primary}`}>Design System</h1>
            </div>
            <div className="flex items-center gap-2">
              <ThemeToggle />
              <span className="px-3 py-1 bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200 text-xs font-medium rounded-full">
                Internal Dev Tool
              </span>
              <a 
                href="/dashboard"
                className={`px-4 py-2 text-sm ${textColors.secondary} ${textColors.hover} ${surfaces.hover} rounded-md transition-colors`}
              >
                ← Back to App
              </a>
            </div>
          </div>
        </Container>
      </header>

      <div className="flex">
        {/* Sidebar Navigation */}
        <nav className={`w-64 ${surfaces.primary} border-r ${borders.default} min-h-[calc(100vh-73px)] sticky top-[73px] overflow-y-auto`}>
          <div className="p-4">
            <h2 className={`font-heading text-xs font-bold ${textColors.muted} uppercase tracking-wider mb-3`}>
              Sections
            </h2>
            <ul className="space-y-1">
              {sections.map((section) => (
                <li key={section.id}>
                  <button
                    onClick={() => setActiveSection(section.id)}
                    className={`w-full text-left px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                      activeSection === section.id
                        ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                        : `${textColors.secondary} ${surfaces.hover}`
                    }`}
                  >
                    {section.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </nav>

        {/* Main Content */}
        <main className="flex-1 p-8">
          <Container size="7xl">
            {activeSection === 'intro' ? (
              <IntroSection />
            ) : activeSection === 'dark-mode' ? (
              <DarkModeGuide />
            ) : ActiveComponent ? (
              <ActiveComponent />
            ) : (
              <div className="text-center py-12">
                <p className={textColors.muted}>Section not found</p>
              </div>
            )}
          </Container>
        </main>
      </div>
    </div>
  );
};

/**
 * IntroSection - Introduction to the design system
 */
const IntroSection = () => {
  return (
    <div className="space-y-12">
      {/* Hero */}
      <section className="text-center py-12">
        <div className="inline-flex items-center justify-center w-20 h-20 bg-blue-100 dark:bg-blue-900/30 rounded-full mb-6">
          <Logo iconSize="w-12 h-12" textSize="text-0" />
        </div>
        <h1 className={`${typography.h1} ${textColors.brightest} mb-4`}>
          MyVitae Design System
        </h1>
        <p className={`text-xl ${textColors.secondary} max-w-2xl mx-auto`}>
          A comprehensive design system for MyVitae's internal startup UI.
          This showcase provides interactive examples and documentation for all components.
        </p>
      </section>

      {/* Warning Banner */}
      <div className="bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-400 dark:border-yellow-600 p-6 rounded-r-lg">
        <div className="flex items-start gap-3">
          <div className="text-2xl">⚠️</div>
          <div>
            <h3 className={`font-heading text-lg font-bold text-yellow-900 dark:text-yellow-200 mb-2`}>
              Internal Developer Tool
            </h3>
            <p className="text-yellow-800 dark:text-yellow-300">
              This showcase page is for <strong>internal development use only</strong>. 
              It should not be accessible to end users in production. Use it to explore components, 
              copy code examples, and understand design system patterns.
            </p>
          </div>
        </div>
      </div>

      {/* What's Included */}
      <section>
        <h2 className={`${typography.h2} ${textColors.bright} mb-6`}>What's Included</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <FeatureCard
            title="Design Tokens"
            description="Colors, typography, spacing, shadows, and other foundational design values"
            icon="🎨"
          />
          <FeatureCard
            title="Brand Assets"
            description="Logo components, brand identity, and usage guidelines"
            icon="🏷️"
          />
          <FeatureCard
            title="Buttons"
            description="Primary, Secondary, Ghost, Danger, Success, and Icon buttons"
            icon="🔘"
          />
          <FeatureCard
            title="Form Inputs"
            description="Input, TextArea, Select, Checkbox, Radio, Toggle, and SearchInput"
            icon="📝"
          />
          <FeatureCard
            title="Cards"
            description="Card layouts with variants, InfoCard for metrics"
            icon="🃏"
          />
          <FeatureCard
            title="Modals"
            description="Modal and ConfirmDialog with accessibility features"
            icon="🪟"
          />
          <FeatureCard
            title="Layout Components"
            description="Container, Section, Panel, Divider, EmptyState"
            icon="📐"
          />
          <FeatureCard
            title="Icons"
            description="100+ icons organized by category with consistent sizing"
            icon="✨"
          />
          <FeatureCard
            title="Documentation"
            description="Code examples, guidelines, and best practices"
            icon="📚"
          />
        </div>
      </section>

      {/* Getting Started */}
      <section>
        <h2 className={`${typography.h2} ${textColors.bright} mb-6`}>Getting Started</h2>
        <div className={`${surfaces.primary} rounded-lg border ${borders.default} p-6`}>
          <h3 className={`${typography.h4} ${textColors.primary} mb-3`}>Import Components</h3>
          <pre className="bg-gray-900 dark:bg-dark-950 text-gray-100 dark:text-dark-200 p-4 rounded-lg text-sm overflow-x-auto">
            <code>{`// Import from design system
import { 
  PrimaryButton, 
  Input, 
  Card, 
  Modal,
  Logo 
} from '../design-system';

// Use in your components
function MyComponent() {
  return (
    <Card>
      <Logo />
      <Input placeholder="Enter text..." />
      <PrimaryButton>Submit</PrimaryButton>
    </Card>
  );
}`}</code>
          </pre>
        </div>
      </section>

      {/* Design Principles */}
      <section>
        <h2 className={`${typography.h2} ${textColors.bright} mb-6`}>Design Principles</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <PrincipleCard
            title="Consistency"
            description="Use design tokens and components to maintain visual consistency across all startup UI."
            icon="🎯"
          />
          <PrincipleCard
            title="Accessibility"
            description="All components meet WCAG 2.1 Level AA standards with proper keyboard navigation and ARIA labels."
            icon="♿"
          />
          <PrincipleCard
            title="Scalability"
            description="Components are designed to be reusable, composable, and easy to extend."
            icon="📈"
          />
          <PrincipleCard
            title="Performance"
            description="Optimized for fast builds and runtime performance with minimal overhead."
            icon="⚡"
          />
        </div>
      </section>

      {/* Scope Note */}
      <section>
        <div className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-400 dark:border-blue-600 p-6 rounded-r-lg">
          <div className="flex items-start gap-3">
            <div className="text-2xl">ℹ️</div>
            <div>
              <h3 className="font-heading text-lg font-bold text-blue-900 dark:text-blue-200 mb-2">
                Scope: Startup UI Only
              </h3>
              <p className="text-blue-800 dark:text-blue-300 mb-3">
                This design system is <strong>exclusively for MyVitae's internal UI</strong> 
                (landing page, dashboard, edit panels, settings, authentication).
              </p>
              <p className="text-blue-800 dark:text-blue-300">
                <strong>User-facing website components</strong> (in the <code className="bg-blue-100 dark:bg-blue-900/50 px-1 rounded">/websites/</code> folder) 
                have their own separate styling system and should NOT use this design system.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Navigation */}
      <section className="text-center py-8">
        <h3 className={`${typography.h4} ${textColors.primary} mb-4`}>
          Ready to explore?
        </h3>
        <p className={`${textColors.secondary} mb-6`}>
          Use the navigation on the left to browse through different sections of the design system.
        </p>
        <div className="text-3xl">👈</div>
      </section>
    </div>
  );
};

/**
 * FeatureCard - Display a feature with icon
 */
const FeatureCard = ({ icon, title, description }) => (
  <div className={`${surfaces.primary} rounded-lg border ${borders.default} p-6 hover:shadow-md transition-shadow`}>
    <div className="text-3xl mb-3">{icon}</div>
    <h3 className={`font-heading text-lg font-bold ${textColors.primary} mb-2`}>{title}</h3>
    <p className={`text-sm ${textColors.secondary}`}>{description}</p>
  </div>
);

/**
 * PrincipleCard - Display a design principle
 */
const PrincipleCard = ({ icon, title, description }) => (
  <div className={`${surfaces.primary} rounded-lg border ${borders.default} p-6`}>
    <div className="flex items-start gap-3">
      <div className="text-2xl">{icon}</div>
      <div>
        <h3 className={`font-heading text-lg font-bold ${textColors.primary} mb-2`}>{title}</h3>
        <p className={`text-sm ${textColors.secondary}`}>{description}</p>
      </div>
    </div>
  </div>
);

/**
 * DarkModeGuide - Comprehensive dark mode documentation
 */
const DarkModeGuide = () => {
  return (
    <div className="space-y-12">
      {/* Hero */}
      <section className="text-center py-12">
        <div className="text-6xl mb-6">🌙</div>
        <h1 className={`${typography.h1} ${textColors.brightest} mb-4`}>
          Dark Mode Guide
        </h1>
        <p className={`text-xl ${textColors.secondary} max-w-2xl mx-auto`}>
          Complete guide to implementing dark mode in MyVitae's internal UI.
          All design system components automatically support both light and dark themes.
        </p>
      </section>

      {/* Quick Start */}
      <section>
        <h2 className={`${typography.h2} ${textColors.bright} mb-6`}>Quick Start</h2>
        <div className={`${surfaces.primary} p-6 rounded-lg border ${borders.default}`}>
          <h3 className={`${typography.h4} ${textColors.primary} mb-4`}>
            Three Simple Rules
          </h3>
          <ol className="space-y-4">
            <li className="flex items-start gap-3">
              <span className="flex-shrink-0 w-8 h-8 bg-blue-500 dark:bg-blue-400 text-white rounded-full flex items-center justify-center font-bold">1</span>
              <div className="flex-1">
                <p className={`${typography.body} ${textColors.primary} font-semibold mb-1`}>
                  Use Theme-Aware Tokens
                </p>
                <p className={`${typography.bodySmall} ${textColors.secondary}`}>
                  Always use <code className="bg-gray-100 dark:bg-dark-800 px-2 py-1 rounded text-xs">surfaces.*</code>, <code className="bg-gray-100 dark:bg-dark-800 px-2 py-1 rounded text-xs">textColors.*</code>, and <code className="bg-gray-100 dark:bg-dark-800 px-2 py-1 rounded text-xs">borders.*</code> instead of hardcoded colors.
                </p>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <span className="flex-shrink-0 w-8 h-8 bg-blue-500 dark:bg-blue-400 text-white rounded-full flex items-center justify-center font-bold">2</span>
              <div className="flex-1">
                <p className={`${typography.body} ${textColors.primary} font-semibold mb-1`}>
                  Import from Design System
                </p>
                <p className={`${typography.bodySmall} ${textColors.secondary}`}>
                  <code className="bg-gray-100 dark:bg-dark-800 px-2 py-1 rounded text-xs">import &#123; surfaces, textColors, typography, borders &#125; from '../design-system/tokens';</code>
                </p>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <span className="flex-shrink-0 w-8 h-8 bg-blue-500 dark:bg-blue-400 text-white rounded-full flex items-center justify-center font-bold">3</span>
              <div className="flex-1">
                <p className={`${typography.body} ${textColors.primary} font-semibold mb-1`}>
                  Test in Both Modes
                </p>
                <p className={`${typography.bodySmall} ${textColors.secondary}`}>
                  Use the theme toggle button (top right) to test your UI in both light and dark modes.
                </p>
              </div>
            </li>
          </ol>
        </div>
      </section>

      {/* Code Examples */}
      <section>
        <h2 className={`${typography.h2} ${textColors.bright} mb-6`}>Code Examples</h2>
        
        <div className="space-y-6">
          {/* Good Example */}
          <div className="bg-green-50 dark:bg-green-900/20 border-2 border-green-500 dark:border-green-700 rounded-lg p-6">
            <div className="flex items-center gap-2 mb-4">
              <span className="text-2xl">✓</span>
              <h3 className={`${typography.h4} text-green-900 dark:text-green-200`}>Good: Using Theme Tokens</h3>
            </div>
            <pre className="bg-gray-900 dark:bg-dark-950 text-gray-100 dark:text-dark-200 p-4 rounded-lg text-sm overflow-x-auto">
              <code>{`import { surfaces, textColors, typography, borders } from '../design-system/tokens';

function MyComponent() {
  return (
    <div className={\`\${surfaces.primary} p-6 rounded-lg border \${borders.default}\`}>
      <h2 className={\`\${typography.h2} \${textColors.bright} mb-4\`}>
        Hello World
      </h2>
      <p className={\`\${typography.body} \${textColors.secondary}\`}>
        This automatically works in both light and dark modes!
      </p>
    </div>
  );
}`}</code>
            </pre>
          </div>

          {/* Bad Example */}
          <div className="bg-red-50 dark:bg-red-900/20 border-2 border-red-500 dark:border-red-700 rounded-lg p-6">
            <div className="flex items-center gap-2 mb-4">
              <span className="text-2xl">✗</span>
              <h3 className={`${typography.h4} text-red-900 dark:text-red-200`}>Bad: Hardcoded Colors</h3>
            </div>
            <pre className="bg-gray-900 dark:bg-dark-950 text-gray-100 dark:text-dark-200 p-4 rounded-lg text-sm overflow-x-auto">
              <code>{`// ❌ DON'T DO THIS - Won't work in dark mode!
function MyComponent() {
  return (
    <div className="bg-white p-6 rounded-lg border border-gray-200">
      <h2 className="text-2xl font-bold text-gray-900 mb-4">
        Hello World
      </h2>
      <p className="text-gray-600">
        This will look broken in dark mode!
      </p>
    </div>
  );
}`}</code>
            </pre>
          </div>
        </div>
      </section>

      {/* Token Reference */}
      <section>
        <h2 className={`${typography.h2} ${textColors.bright} mb-6`}>Token Reference</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Surfaces */}
          <div className={`${surfaces.primary} p-6 rounded-lg border ${borders.default}`}>
            <h3 className={`${typography.h4} ${textColors.primary} mb-4`}>Surfaces</h3>
            <div className="space-y-2 text-sm font-mono">
              <div className={`p-2 rounded ${surfaces.primary} border ${borders.default}`}>surfaces.primary</div>
              <div className={`p-2 rounded ${surfaces.secondary}`}>surfaces.secondary</div>
              <div className={`p-2 rounded ${surfaces.tertiary}`}>surfaces.tertiary</div>
              <div className={`p-2 rounded ${surfaces.elevated}`}>surfaces.elevated</div>
            </div>
          </div>

          {/* Text Colors */}
          <div className={`${surfaces.primary} p-6 rounded-lg border ${borders.default}`}>
            <h3 className={`${typography.h4} ${textColors.primary} mb-4`}>Text Colors</h3>
            <div className="space-y-2">
              <div className={`${textColors.brightest} font-semibold`}>textColors.brightest</div>
              <div className={`${textColors.bright}`}>textColors.bright</div>
              <div className={`${textColors.primary}`}>textColors.primary</div>
              <div className={`${textColors.secondary}`}>textColors.secondary</div>
              <div className={`${textColors.tertiary}`}>textColors.tertiary</div>
              <div className={`${textColors.muted}`}>textColors.muted</div>
            </div>
          </div>

          {/* Borders */}
          <div className={`${surfaces.primary} p-6 rounded-lg border ${borders.default}`}>
            <h3 className={`${typography.h4} ${textColors.primary} mb-4`}>Borders</h3>
            <div className="space-y-3">
              <div className={`p-3 border-2 ${borders.light} rounded`}>borders.light</div>
              <div className={`p-3 border-2 ${borders.default} rounded`}>borders.default</div>
              <div className={`p-3 border-2 ${borders.strong} rounded`}>borders.strong</div>
            </div>
          </div>

          {/* Typography */}
          <div className={`${surfaces.primary} p-6 rounded-lg border ${borders.default}`}>
            <h3 className={`${typography.h4} ${textColors.primary} mb-4`}>Typography</h3>
            <div className="space-y-2">
              <div className={`${typography.h1} ${textColors.brightest}`} style={{fontSize: '1.5rem'}}>typography.h1</div>
              <div className={`${typography.h3} ${textColors.primary}`}>typography.h3</div>
              <div className={`${typography.body} ${textColors.secondary}`}>typography.body</div>
              <div className={`${typography.bodySmall} ${textColors.tertiary}`}>typography.bodySmall</div>
            </div>
          </div>
        </div>
      </section>

      {/* Live Example */}
      <section>
        <h2 className={`${typography.h2} ${textColors.bright} mb-6`}>Live Example</h2>
        <p className={`${typography.body} ${textColors.secondary} mb-6`}>
          This card demonstrates proper dark mode implementation. Toggle the theme (top right) to see it adapt:
        </p>
        
        <div className={`${surfaces.primary} p-8 rounded-lg border ${borders.default} shadow-md`}>
          <h3 className={`${typography.h3} ${textColors.bright} mb-4`}>
            Example Card
          </h3>
          <p className={`${typography.body} ${textColors.secondary} mb-4`}>
            This card uses all the right tokens, so it looks perfect in both light and dark modes.
            No manual dark: classes needed!
          </p>
          <div className="flex gap-3">
            <button className="px-4 py-2 bg-blue-600 dark:bg-blue-500 text-white rounded-md hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors">
              Primary Action
            </button>
            <button className={`px-4 py-2 ${surfaces.secondary} ${textColors.primary} rounded-md border ${borders.default} hover:${surfaces.tertiary} transition-colors`}>
              Secondary Action
            </button>
          </div>
        </div>
      </section>

      {/* Best Practices */}
      <section>
        <h2 className={`${typography.h2} ${textColors.bright} mb-6`}>Best Practices</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-6">
            <h3 className="font-heading font-bold text-green-900 dark:text-green-200 mb-3">✓ Do</h3>
            <ul className="space-y-2 text-sm text-green-800 dark:text-green-300">
              <li>• Use theme-aware tokens for all colors</li>
              <li>• Test in both light and dark modes</li>
              <li>• Use design system components</li>
              <li>• Import tokens at the top of files</li>
              <li>• Follow the token naming convention</li>
            </ul>
          </div>

          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6">
            <h3 className="font-heading font-bold text-red-900 dark:text-red-200 mb-3">✗ Don't</h3>
            <ul className="space-y-2 text-sm text-red-800 dark:text-red-300">
              <li>• Use hardcoded colors (bg-white, text-gray-900)</li>
              <li>• Manually add dark: classes everywhere</li>
              <li>• Mix token and non-token approaches</li>
              <li>• Forget to test in dark mode</li>
              <li>• Use bg-gray-50 instead of surfaces.secondary</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Migration Guide */}
      <section>
        <h2 className={`${typography.h2} ${textColors.bright} mb-6`}>Migration Guide</h2>
        <div className={`${surfaces.primary} p-6 rounded-lg border ${borders.default}`}>
          <h3 className={`${typography.h4} ${textColors.primary} mb-4`}>
            Converting Existing Components
          </h3>
          <div className="space-y-4">
            <div>
              <p className={`${typography.body} ${textColors.secondary} mb-2 font-semibold`}>
                Step 1: Add imports
              </p>
              <pre className="bg-gray-100 dark:bg-dark-800 p-3 rounded text-xs overflow-x-auto">
                <code>import &#123; surfaces, textColors, typography, borders &#125; from '../design-system/tokens';</code>
              </pre>
            </div>
            <div>
              <p className={`${typography.body} ${textColors.secondary} mb-2 font-semibold`}>
                Step 2: Replace common patterns
              </p>
              <ul className="space-y-2 text-sm">
                <li>• <code className="bg-gray-100 dark:bg-dark-800 px-2 py-1 rounded">bg-white</code> → <code className="bg-gray-100 dark:bg-dark-800 px-2 py-1 rounded">{`\${surfaces.primary}`}</code></li>
                <li>• <code className="bg-gray-100 dark:bg-dark-800 px-2 py-1 rounded">text-gray-900</code> → <code className="bg-gray-100 dark:bg-dark-800 px-2 py-1 rounded">{`\${textColors.primary}`}</code></li>
                <li>• <code className="bg-gray-100 dark:bg-dark-800 px-2 py-1 rounded">border-gray-200</code> → <code className="bg-gray-100 dark:bg-dark-800 px-2 py-1 rounded">{`\${borders.default}`}</code></li>
              </ul>
            </div>
            <div>
              <p className={`${typography.body} ${textColors.secondary} mb-2 font-semibold`}>
                Step 3: Test thoroughly
              </p>
              <p className={`${typography.bodySmall} ${textColors.tertiary}`}>
                Toggle between light and dark modes to ensure everything looks correct.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ShowcasePage;
